import { useCallback, useEffect, useRef, useState } from 'react';
// utils
import {
  deletePendingFeedOrder,
  getLastSubmittedFeedOrder,
  getPendingFeedOrders,
} from 'utils/offlineFeedOrdersHelper';

const FEED_ORDERS_SYNC_KEYS = ['syncingFeedOrders', 'syncingDCAndFeedOrders', 'feedOrdersNotSynced'];

// Orders placed offline that have not reached the server yet, plus the last order the server received
// while there is no connection to list it from (EPN-3082).
// onSynced runs once after NetworkManager finished sending them, to refetch the server list.
export default function usePendingFeedOrders({
  userId, companyId, farmId, isOnline, syncProgress, syncingData, onSynced,
}) {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [lastSubmittedOrder, setLastSubmittedOrder] = useState(null);
  const prevSyncProgress = useRef(syncProgress);

  const loadOrders = useCallback(() => {
    const scope = { userId, companyId, farmId };
    return Promise.all([
      getPendingFeedOrders(scope).catch(() => []),
      getLastSubmittedFeedOrder(scope).catch(() => null),
    ]).then(([pending, submitted]) => {
      setPendingOrders(pending);
      setLastSubmittedOrder(submitted);
    });
  }, [userId, companyId, farmId]);

  const removePendingOrder = useCallback((clientUuid) => (
    deletePendingFeedOrder(clientUuid).then(loadOrders)
  ), [loadOrders]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    const isJustFinished = syncProgress === 100 && prevSyncProgress.current !== 100;
    prevSyncProgress.current = syncProgress;
    if (isJustFinished && FEED_ORDERS_SYNC_KEYS.includes(syncingData)) {
      loadOrders();
      onSynced?.();
    }
  }, [syncProgress, syncingData]);

  // The server list has the order once there is a connection
  const submittedOrders = !isOnline && lastSubmittedOrder ? [lastSubmittedOrder] : [];

  return { pendingOrders, submittedOrders, removePendingOrder };
}
